import React from 'react';
import {
  Card,
  Typography,
  Skeleton,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Pagination,
  Box,
} from '@mui/material';
import Iconify from '../../iconify/Iconify';

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString()}`;

export default function EmployeePerformanceTable({
  rows,
  pageInfo,
  loading,
  error,
  onRetry,
  onPageChange,
}) {
  return (
    <Card
      sx={{
        p: 2.25,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 8px 22px rgba(16, 24, 40, 0.05)',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Iconify icon="solar:users-group-rounded-bold-duotone" width={18} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Employee Performance
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: { xs: '0.72rem', md: '0.75rem' } }}>
        Delivery and to-pay outcomes by employee
      </Typography>

      {error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          Failed to load employee performance.
        </Alert>
      ) : loading ? (
        <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
      ) : (
        <>
          <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(25,118,210,0.06)' }}>
                  <TableCell align="center">SNo</TableCell>
                  <TableCell align="center">Employee</TableCell>
                  <TableCell align="center">Deliveries</TableCell>
                  <TableCell align="center">Delivered</TableCell>
                  <TableCell align="center">Failed</TableCell>
                  <TableCell align="center">ToPay Collected</TableCell>
                  <TableCell align="center">ToPay Pending</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(rows || []).map((row, index) => (
                  <TableRow
                    key={row.employeeId || `${row.employeeName}-${index}`}
                    hover
                    sx={{ '&:nth-of-type(even)': { bgcolor: 'rgba(0,0,0,0.01)' } }}
                  >
                    <TableCell align="center">{(Number(pageInfo?.current_page || 1) - 1) * Number(pageInfo?.per_page || 10) + index + 1}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.8} alignItems="center" justifyContent="center">
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'primary.main' }} />
                        <span>{row.employeeName || 'NA'}</span>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">{row.deliveries || row.deliveriesCount || 0}</TableCell>
                    <TableCell align="center">{row.delivered || 0}</TableCell>
                    <TableCell align="center">{row.failed || row.deliveryFailed || 0}</TableCell>
                    <TableCell align="center">{formatCurrency(row.toPayCollected || row.collected)}</TableCell>
                    <TableCell align="center">{formatCurrency(row.toPayPending || row.pending)}</TableCell>
                  </TableRow>
                ))}
                {!(rows || []).length ? (
                  <TableRow>
                    <TableCell align="center" colSpan={7} sx={{ height: 120 }}>
                      No employee performance records found
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          {Number(pageInfo?.total_pages || 0) > 1 ? (
            <Stack alignItems="center" sx={{ pt: 2 }}>
              <Pagination
                color="primary"
                page={Number(pageInfo?.current_page || 1)}
                count={Number(pageInfo?.total_pages || 1)}
                onChange={onPageChange}
              />
            </Stack>
          ) : null}
        </>
      )}
    </Card>
  );
}
